'use es6';

import { combineReducers } from 'redux';
import SeamlessImmutable from 'seamless-immutable';
import { FINDER_ASSET_DESELECT, FINDER_ASSET_TOGGLE_SELECTION, FINDER_DESELECT_ALL, FINDER_FILE_DELETE, FINDER_FILES_BULK_DELETE, FINDER_FOLDER_DELETE, FINDER_FOLDERS_COLLAPSE_ALL, FINDER_FOLDERS_BULK_DELETE, FINDER_FOLDER_CONTENTS_BULK_DELETE, FINDER_FOLDER_CONTENTS_MOVE, FINDER_ASSET_TOGGLE_SELECTIONS, FINDER_ASSET_REVEAL_IN_FINDER, TAB_OPEN } from 'DesignData/actions/ActionTypes';
const EMPTY_SELECTED_ASSETS = SeamlessImmutable({
  order: [],
  byId: {},
  lastSelected: null,
  lastDeselected: null
});

function removeSelectedAsset(state, assetId) {
  if (!state.byId[assetId]) {
    return state;
  }

  const removeIndex = state.order.indexOf(assetId);
  const order = [...state.order.slice(0, removeIndex), ...state.order.slice(removeIndex + 1)];
  const byId = state.byId.without(assetId);
  const lastSelected = order[removeIndex] || order[removeIndex - 1];
  return state.merge({
    order,
    byId,
    lastSelected,
    lastDeselected: assetId
  });
}

function selectedAssets(state = EMPTY_SELECTED_ASSETS, action) {
  switch (action.type) {
    case FINDER_DESELECT_ALL:
    case FINDER_FOLDERS_COLLAPSE_ALL:
      {
        return EMPTY_SELECTED_ASSETS;
      }

    case FINDER_ASSET_DESELECT:
      {
        return removeSelectedAsset(state, action.assetId);
      }

    case FINDER_ASSET_TOGGLE_SELECTION:
      {
        const {
          assetId,
          assetType,
          parentFolderId,
          isMultiSelect
        } = action;

        if (state.byId[assetId] && isMultiSelect) {
          return removeSelectedAsset(state, assetId);
        }

        const assetEntry = {
          assetId,
          assetType,
          parentFolderId
        };

        if (!isMultiSelect) {
          return state.merge({
            order: [assetId],
            byId: {
              [assetId]: assetEntry
            },
            lastSelected: assetId
          });
        }

        const order = state.order.concat(assetId);
        const byId = state.byId.set(assetId, assetEntry);
        return state.merge({
          order,
          byId,
          lastSelected: assetId,
          lastDeselected: null
        });
      }

    case FINDER_ASSET_TOGGLE_SELECTIONS:
      {
        const {
          assets,
          assetIds,
          shiftMultiSelect
        } = action;

        if (!shiftMultiSelect) {
          const byId = assets.reduce((__byId, asset) => {
            __byId[asset.assetId] = asset;
            return __byId;
          }, {});
          return state.merge({
            byId,
            order: assetIds,
            lastDeselected: null
          });
        }

        let order = state.order;
        const byId = assets.reduce((__byId, asset) => {
          if (state.byId[asset.assetId]) {
            return __byId;
          }

          order = order.concat([asset.assetId]);
          return __byId.set(asset.assetId, asset);
        }, state.byId);
        return state.merge({
          order,
          byId,
          shiftMultiSelect
        });
      }

    case FINDER_FILE_DELETE:
      {
        const {
          fileId,
          deleted
        } = action;
        if (!deleted) return state;
        return removeSelectedAsset(state, fileId);
      }

    case FINDER_FILES_BULK_DELETE:
      {
        const {
          results
        } = action;
        if (!results) return state;
        return results.reduce((updatedState, result) => {
          return result.succeeded ? removeSelectedAsset(updatedState, result.id) : updatedState;
        }, state);
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          folderId,
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        const {
          files = [],
          subfolders = []
        } = contents;
        const deletedAssetIds = [folderId].concat(files).concat(subfolders);
        return deletedAssetIds.reduce(removeSelectedAsset, state);
      }

    case FINDER_FOLDERS_BULK_DELETE:
      {
        const {
          results
        } = action;

        if (!results) {
          return state;
        }

        const deletedFolderIds = results.reduce((__deletedFolderIds, result) => {
          if (result.succeeded) {
            __deletedFolderIds.push(result.id);
          }

          return __deletedFolderIds;
        }, []);
        return deletedFolderIds.reduce(removeSelectedAsset, state);
      }

    case FINDER_FOLDER_CONTENTS_BULK_DELETE:
      {
        const {
          files = [],
          subfolders = []
        } = action;
        const assets = files.concat(subfolders);
        const deletedAssets = assets.map(__asset => __asset.id);
        return deletedAssets.reduce(removeSelectedAsset, state);
      }

    case FINDER_FOLDER_CONTENTS_MOVE:
      {
        const {
          folderId,
          results
        } = action;
        if (!results) return state;
        return results.reduce((updatedState, asset) => {
          const selection = updatedState.byId[asset.id];

          if (!selection || asset.cloned) {
            return updatedState;
          }

          return updatedState.setIn(['byId', asset.id, 'parentFolderId'], folderId);
        }, state);
      }

    default:
      {
        return state;
      }
  }
}

function revealedAssetId(state = null, action) {
  switch (action.type) {
    case FINDER_ASSET_TOGGLE_SELECTION:
      {
        if (action.assetId === state) return state;
        return null;
      }

    case FINDER_ASSET_REVEAL_IN_FINDER:
      {
        return action.assetId;
      }

    case TAB_OPEN:
      {
        return null;
      }

    default:
      {
        return state;
      }
  }
}

export default combineReducers({
  selectedAssets,
  revealedAssetId
});